import { FEATURE_FLAG } from '@qality/common-config';
import { TestCycleProject } from '@qality/common-model';

const ADDON_KEY = plugin.addonKey;

export default {
  getIssuesUrl(params) {
    const hostName = getHost();
    let issuesURL = `${hostName}/issues/?`;

    if (params?.jql) {
      issuesURL += `jql=${encodeURIComponent(params.jql)}&`;
    }

    if (params?.filter) {
      issuesURL += `filter=${encodeURIComponent(params.filter)}&`;
    }

    return issuesURL;
  },

  getBrowseIssueUrl(issueKey) {
    return getHost() + '/browse/' + issueKey;
  },

  async getEmptyTestCaseExecutionUrl() {
    return (
      getHost() +
      '/secure/TestCaseExecutionAction!execute.jspa?testExecutionId='
    );
  },

  getTestCaseExecutionUrl(testExecutionId) {
    return (
      getHost() +
      '/secure/TestCaseExecutionAction!execute.jspa?testExecutionId=' +
      testExecutionId
    );
  },

  getTestCaseExecutionHistoryUrl(testCaseKey, issueKey, pageNumber = 0) {
    return (
      getHost() +
      `/secure/ExecutionHistoryAction!execute.jspa?testCaseKey=${testCaseKey}&issueKey=${issueKey}&pageNumber=${pageNumber}`
    );
  },

  getTestCycleUrl(testCycleId, addedTestCases) {
    let url =
      getHost() +
      `/secure/TestCycleAction!show.jspa?testCycleId=${testCycleId}`;

    if (addedTestCases) {
      url += `&addedTC=${addedTestCases}`;
    }

    return url;
  },

  async getTestCyclesUrl(projectKey) {
    return (
      getHost() + `/secure/TestCyclesAction!show.jspa?projectKey=${projectKey}`
    );
  },

  async getProjectDashboardUrl(projectKey, projectId) {
    return (
      getHost() +
      `/secure/DashboardAction!show.jspa?projectKey=${projectKey}&projectId=${projectId}`
    );
  },

  async getGlobalDashboardUrl() {
    return getHost() + '/secure/DashboardAction!show.jspa';
  },

  async getTestCasesOverviewLink() {
    return (
      getHost() +
      "/issues/?jql=issuetype %3D 'QAlity Test' order by created DESC"
    );
  },

  getTestCasesImportLink() {
    return getHost() + `/secure/ImportTestCasesAction!show.jspa`;
  },

  getProjectUrl(projectKey) {
    return getHost() + `/projects/${projectKey}`;
  },

  getAvatarUrl(img: string) {
    return getHost() + img;
  },

  getUrlToProjectIcon(project) {
    return getHost() + `/secure/projectavatar?avatarId=${project.icon.id}`;
  },

  getVersionUrl(projectKey, versionId) {
    return getHost() + `/browse/${projectKey}/fixforversion/${versionId}`;
  },

  getViewAttachmentSettingsUrl() {
    return getHost() + '/secure/admin/ViewAttachmentSettings.jspa';
  },

  getProjectConfigIssueTypesUrl(project) {
    return (
      getHost() + `/plugins/servlet/project-config/${project.key}/issuetypes`
    );
  },

  getURLSearchParams(): URLSearchParams {
    let jiraLocation;
    if (window.frameElement !== null && window.frameElement !== undefined) {
      jiraLocation = window.parent.location;
    } else {
      jiraLocation = window.location;
    }

    return new URLSearchParams(new URL(jiraLocation.href).search);
  },

  getAllProjectsURL() {
    return (
      getHost() +
      '/secure/BrowseProjects.jspa?selectedCategory=all&selectedProjectType=all'
    );
  },

  getNextGenIssueTypeConfigUrl() {
    return ``;
  },

  getProjectURL(projectKey) {
    return getHost() + `/projects/${projectKey}/issues`;
  },

  getReportsURL(projectKey) {
    return (
      getHost() +
      `/projects/${projectKey}?selectedItem=com.atlassian.jira.jira-projects-plugin:report-page`
    );
  },

  getIssueURL(issueKey) {
    return getHost() + '/browse/' + issueKey;
  },

  setUrlParams(params) {
    this.updateUrlParams(params);
  },

  updateUrlParams(params) {
    const searchParams = this.getURLSearchParams();
    params.forEach(function (param) {
      if (param.omit) {
        searchParams.delete(param.name);
      } else {
        searchParams.set(param.name, param.value);
      }
    });
    const url =
      window.location.origin +
      window.location.pathname +
      '?' +
      searchParams.toString();
    window.history.pushState({}, null, url);
  },

  getQAlityIssueIcon() {
    const contextPath = window.AJS.contextPath();
    return `${contextPath}/download/resources/${ADDON_KEY}:downloadable-resources/images/qality-issue-icon.png`;
  },

  getQAlityPlusImage() {
    const contextPath = window.AJS.contextPath();
    return `${contextPath}/download/resources/${ADDON_KEY}:downloadable-resources/images/qality-plus.svg`;
  },

  getFileUploadIconPath() {
    const contextPath = window.AJS.contextPath();
    return `${contextPath}/download/resources/${ADDON_KEY}:downloadable-resources/images/file-upload-icon.svg`;
  },

  getTraceabilityReportPageCoverPath() {
    const contextPath = window.AJS.contextPath();
    return `${contextPath}/download/resources/${ADDON_KEY}:downloadable-resources/images/traceabilityReportPageCover.svg`;
  },

  getLeftArrowIconPath() {
    const contextPath = window.AJS.contextPath();
    return `${contextPath}/download/resources/${ADDON_KEY}:downloadable-resources/images/LeftArrow.svg`;
  },

  getRightArrowIconPath() {
    const contextPath = window.AJS.contextPath();
    return `${contextPath}/download/resources/${ADDON_KEY}:downloadable-resources/images/RightArrow.svg`;
  },

  getArrowWithShadowIcon() {
    const contextPath = window.AJS.contextPath();
    return `${contextPath}/download/resources/${ADDON_KEY}:downloadable-resources/images/arrow-with-shadow.svg`;
  },

  getUrlToSearchIssues(issues: any) {
    return getHost() + '/issues/?jql=%20key%20in(' + issues.join() + ')';
  },

  getJiraURLSearchParams() {
    let urlLocation;
    if (window.frameElement !== null && window.frameElement !== undefined) {
      urlLocation = window.parent.location;
    } else {
      urlLocation = window.location;
    }

    const search = new URL(urlLocation).search;

    return new URLSearchParams(search);
  },

  getSettingsUrl() {
    const appSuffix = FEATURE_FLAG.isPremium ? 'Plus' : '';
    return (
      getHost() + `/secure/SettingsAction${appSuffix}!settings${appSuffix}.jspa`
    );
  },

  getQalityPlusMarketplaceUrl() {
    return `https://marketplace.atlassian.com/apps/1223344/qality-plus-test-management-for-jira?hosting=server&tab=overview&utm_source=qality&utm_medium=referral`;
  },

  getQalityVersionsComparisonUrl() {
    return `https://soldevelo.atlassian.net/l/c/cGDekdrn`;
  },

  buildUrlWithParams: (urlBase: string, urlParams: Array<string>): string => {
    return [urlBase, urlParams.join('&')].join('?');
  },

  getProjectAvatarUrl(projectId: string, iconId: string): string {
    return (
      getHost() +
      `/secure/projectavatar?size=small&pid=${projectId}&avatarId=${iconId}`
    );
  },

  getManageAppUrl(): string {
    return `${getHost()}/plugins/servlet/upm?source=side_nav_manage_addons`;
  },

  getStatusDocumentationUrl(): string {
    return 'https://soldevelo.atlassian.net/wiki/spaces/QS/pages/4581425174/Custom+Execution+Statuses';
  },
  getTestExecutionReportUrl(
    testCycleId: string,
    testCycleProject: TestCycleProject
  ): string {
    const urlBase = `${getHost()}/secure/ConfigureReport.jspa?reportName=TestExecutionReport`;
    const reportKey = `com.soldevelo.apps.test_management_premium%3A${plugin.testExecutionReportKey}`;
    return `${urlBase}&selectedProjectId=${testCycleProject.id}&reportKey=${reportKey}&startDate=none&endDate=none&projectId=ALL_PROJECTS&testCyclesIds=${testCycleId}`;
  },
};

function getHost() {
  return window.location.origin + window.AJS.contextPath();
}
